import { createClient as createConnectClient } from '@connectrpc/connect'
import { getCurrentTenant, getSessionToken } from '@descope/react-sdk'

import { CustomerManagementService } from '@/gen/customer/management/v1/service_pb'
import { AssessmentService } from '@/gen/inventory/v1/assessment_service_pb'

import { sessionTokenInterceptor } from '@/lib/auth/session-token-interceptor'
import { getConnectTransport } from '@/lib/connect/connect-transport'

import type { CommentsUserMeta } from './types'
import { filterMentionSuggestions } from './utils'

const transport = getConnectTransport({ interceptors: [sessionTokenInterceptor] })
const assessmentClient = createConnectClient(AssessmentService, transport)
const managementClient = createConnectClient(CustomerManagementService, transport)

const INTERNAL_ROLES = new Set(['Admin', 'Controls Admin', 'Member', 'Viewer'])

type MentionableUser = { info: CommentsUserMeta['info']; isInternal: boolean }

let usersByEmail: Promise<Map<string, MentionableUser>> | null = null
let loadedUsers: Map<string, MentionableUser> | null = null

let sessionTenantSettled = false
let resolveSessionTenant: (() => void) | null = null
let sessionTenantReady: Promise<void> | null = null

// A token with no org yet. TenantCommentsProvider settles this from useSession.
const isAwaitingTenant = () => {
  if (sessionTenantSettled) return false
  try {
    const token = getSessionToken()
    return !!token && !getCurrentTenant(token)
  } catch {
    return false
  }
}

export const settleSessionTenant = () => {
  sessionTenantSettled = true
  resolveSessionTenant?.()
  resolveSessionTenant = null
}

const waitForSessionTenant = () => {
  if (!isAwaitingTenant()) return Promise.resolve()
  sessionTenantReady ??= new Promise<void>((resolve) => {
    resolveSessionTenant = resolve
  })
  return sessionTenantReady
}

const loadUsers = () => {
  if (!usersByEmail) {
    usersByEmail = waitForSessionTenant()
      .then(() => managementClient.listUsers({}))
      .then(({ users }) => {
        const map = new Map<string, MentionableUser>()
        for (const user of users) {
          map.set(user.email, {
            info: { name: user.name || user.email, avatar: user.avatarUrl },
            isInternal: INTERNAL_ROLES.has(user.role),
          })
        }
        loadedUsers = map
        return map
      })
      .catch((error: unknown) => {
        // Drop the rejected promise, otherwise it stays cached and every author
        // renders as their raw user id until the page is reloaded.
        usersByEmail = null
        throw error
      })
  }
  return usersByEmail
}

export const loadCommentUsers = () => loadUsers()

export const isUserInternal = (userId: string) => loadedUsers?.get(userId)?.isInternal ?? false

const namesFor = (users: Map<string, MentionableUser>, userIds: readonly string[]) =>
  // Portal recipients are never in listUsers; their id is their email (rooms.go, LiveblocksUserID).
  userIds.map((id) => users.get(id)?.info ?? { name: id })

// `| undefined` keeps the portal's resolver assignable in serializeCommentBody.
export const resolveUsers = async ({
  userIds,
}: {
  userIds: readonly string[]
}): Promise<(CommentsUserMeta['info'] | undefined)[]> => {
  try {
    return namesFor(await loadUsers(), userIds)
  } catch {
    // Liveblocks will not ask again after this throws, so try once more now that the
    // failed directory read has been dropped from the cache.
    return namesFor(await loadUsers(), userIds)
  }
}

export const resolveMentionSuggestions = async ({ text }: { text: string }) => {
  const users = await loadUsers()
  return filterMentionSuggestions(
    [...users.entries()].map(([email, { info }]) => ({ name: info.name, email })),
    text,
  )
}

export const commentsAuthEndpoint = async () => {
  const { token } = await assessmentClient.getToken({})
  return { token }
}
